import { getPropertyPriceLabel, hasPublicPrice } from '../../utils/propertyPrice';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Edit2, Trash2, Eye, Plus, Search, AlignJustify, Filter, ArrowDownUp, GripVertical, ArrowUp, ArrowDown, Link2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { PLACEHOLDER_IMAGE } from '../../utils/imageProxy';
import { buildPreviewUrl } from '../../utils/previewToken';

function PropertiesList() {
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState({
    statusId: '',
    type: '',
    query: '',
    zone: '',
    sortBy: 'priority', // Default sorting by priority
    sortOrder: 'asc' as 'asc' | 'desc' // Default ascending order for priority
  });
  const [zones, setZones] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  
  // Drag and drop states
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // Ref to track if order has changed
  const orderChangedRef = useRef(false);
  // Debounce timer for saving
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // L'incorporato uno-a-uno può arrivare come oggetto o come array a un elemento.
  const previewTokenOf = (property: any): string | null => {
    const link = property?.property_preview_links;
    if (!link) return null;
    return Array.isArray(link) ? link[0]?.token ?? null : link.token ?? null;
  };

  // Copia rapida del link privato, per non dover aprire la scheda.
  const copyPreviewLink = async (token: string) => {
    const url = buildPreviewUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link privato copiato');
    } catch {
      window.prompt('Copia il link:', url);
    }
  };

  useEffect(() => {
    fetchZones();
    fetchStatuses();
  }, []);

  // Fetch properties once statuses are loaded, and when sort/filter changes
  useEffect(() => {
    if (statuses.length > 0) {
      fetchProperties();
    }
  }, [statuses, filter.sortBy, filter.sortOrder]);

  const fetchZones = async () => {
    try {
      const { data, error } = await supabase
        .from('zones')
        .select('*, provinces(*)');

      if (error) {
        console.error('Error fetching zones:', error);
      } else {
        setZones(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStatuses = async () => {
    try {
      const { data, error } = await supabase
        .from('property_statuses')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching statuses:', error);
      } else {
        setStatuses(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchProperties = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('properties')
        .select(`
          *,
          zones(*),
          provinces(*),
          property_images(*),
          property_features(
            features(*)
          ),
          property_types(*),
          property_states(*),
          property_statuses(*),
          property_preview_links(token)
        `);

      // Apply filters
      if (filter.statusId) {
        query = query.eq('status_id', filter.statusId);
      } else {
        // Hide archived properties by default
        const archivedStatus = statuses.find(s => s.name === 'archived');
        if (archivedStatus) {
          query = query.neq('status_id', archivedStatus.id);
        }
      }

      if (filter.type) {
        query = query.eq('type', filter.type);
      }

      if (filter.zone) {
        query = query.eq('zone_id', filter.zone);
      }

      if (filter.query) {
        query = query.ilike('title', `%${filter.query}%`);
      }

      // Apply sorting
      query = query.order(filter.sortBy, { ascending: filter.sortOrder === 'asc' })
                   .order('created_at', { ascending: false }); // Secondary sort by creation date

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Transform data to include primary image URL
      const transformedData = (data || []).map(property => {
        // Sort images: primary first, then by position
        const sortedImages = [...(property.property_images || [])].sort((a, b) => {
          if (a.is_primary && !b.is_primary) return -1;
          if (!a.is_primary && b.is_primary) return 1;
          return (a.position || 0) - (b.position || 0);
        });
        
        const primaryImage = sortedImages.length > 0 ? sortedImages[0].url : null;
        
        return {
          ...property,
          images: sortedImages.map((img: any) => img.url) || [],
          primaryImage: primaryImage || PLACEHOLDER_IMAGE
        };
      });

      setProperties(transformedData);
    } catch (err: any) {
      console.error('Error fetching properties:', err);
      toast.error(`Errore: ${err.message || 'Si è verificato un errore durante il caricamento'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo immobile?')) return;
    
    try {
      // Delete the property (cascade will delete related records)
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Immobile eliminato con successo');
      // Refresh properties list
      fetchProperties();
    } catch (error: any) {
      console.error('Error deleting property:', error);
      toast.error(`Errore: ${error.message}`);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    fetchProperties();
  };

  const handleSort = (field: string) => {
    setFilter(prev => {
      if (prev.sortBy === field) {
        return { ...prev, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' };
      }
      return { ...prev, sortBy: field, sortOrder: 'desc' };
    });
  };

  const handleResetFilters = () => {
    setFilter({
      statusId: '',
      type: '',
      query: '',
      zone: '',
      sortBy: 'priority',
      sortOrder: 'asc'
    });
  };

  // Save the new order to the database
  const saveOrder = async () => {
    if (!orderChangedRef.current) return;
    
    setSavingOrder(true);
    try {
      // Update priority values for each property based on their position
      const updates = properties.map((property, index) => ({
        id: property.id,
        priority: index
      }));

      // Use Promise.all to update all properties in parallel
      await Promise.all(
        updates.map(update => 
          supabase
            .from('properties')
            .update({ priority: update.priority })
            .eq('id', update.id)
        )
      );

      toast.success('Ordine immobili aggiornato');
      orderChangedRef.current = false;
    } catch (error: any) {
      console.error('Error saving order:', error);
      toast.error(`Errore: ${error.message}`);
    } finally {
      setSavingOrder(false);
    }
  };

  // Debounced save function
  const debouncedSaveOrder = () => {
    // Clear any existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Set a new timer
    saveTimerRef.current = setTimeout(() => {
      saveOrder();
    }, 1000); // Wait 1 second before saving
  };

  // Drag and Drop functionality
  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, id: string) => {
    setDraggedItemId(id);
    setIsDragging(true);
    
    // Create a custom drag image
    const dragPreview = document.createElement('div');
    dragPreview.classList.add('drag-preview');
    dragPreview.innerHTML = `
      <div style="
        padding: 8px 16px;
        background: #1D2B4E;
        color: white;
        border-radius: 6px;
        font-weight: bold;
        box-shadow: 0 4px 10px rgba(0,0,0,0.2);
      ">
        <div style="display: flex; align-items: center;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 8px;"><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="9" cy="6" r="1"/><circle cx="15" cy="6" r="1"/><circle cx="9" cy="18" r="1"/><circle cx="15" cy="18" r="1"/></svg>
          Riordino immobile...
        </div>
      </div>
    `;
    
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 20, 20);
    
    // Remove the element after drag starts
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
    
    e.currentTarget.classList.add('opacity-50', 'bg-gray-100');
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, id: string) => {
    e.preventDefault();
    
    if (id !== draggedItemId && draggedItemId !== null) {
      setDraggedOverId(id);
      
      // Find indices of dragged and target items
      const draggedIndex = properties.findIndex(p => p.id === draggedItemId);
      const targetIndex = properties.findIndex(p => p.id === id);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Create a new array with the item moved to the new position
        const newProperties = [...properties];
        const [draggedItem] = newProperties.splice(draggedIndex, 1);
        newProperties.splice(targetIndex, 0, draggedItem);
        
        // Update the properties array with new order
        setProperties(newProperties);
        orderChangedRef.current = true;
      }
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.currentTarget.classList.remove('opacity-50', 'bg-gray-100');
    setIsDragging(false);
    setDraggedItemId(null);
    setDraggedOverId(null);
    
    // Save the order if it changed
    if (orderChangedRef.current) {
      debouncedSaveOrder();
    }
  };

  // Alternativa touch al drag&drop: su mobile gli eventi HTML5 drag non si
  // attivano, quindi il riordino avviene con i pulsanti su/giù delle card.
  // Riusa lo stesso percorso di salvataggio del drag (splice + debouncedSaveOrder).
  const moveProperty = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= properties.length) return;

    const newProperties = [...properties];
    const [moved] = newProperties.splice(index, 1);
    newProperties.splice(targetIndex, 0, moved);

    setProperties(newProperties);
    orderChangedRef.current = true;
    debouncedSaveOrder();
  };

  return (
    <AdminLayout title="Gestione Immobili">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold">Tutti gli Immobili</h2>
          <p className="text-sm text-gray-500 mt-1 hidden md:block">
            Trascina le righe per modificare l'ordine di visualizzazione degli immobili
          </p>
          <p className="text-sm text-gray-500 mt-1 md:hidden">
            Usa le frecce sulle schede per modificare l'ordine di visualizzazione
          </p>
        </div>
        <div className="flex space-x-3 flex-shrink-0">
          <a
            href="/admin/properties/new"
            className="bg-primary hover:bg-primary-light text-white px-4 py-2.5 rounded-md transition-colors flex items-center justify-center w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuovo Immobile
          </a>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-1 relative">
            <input
              type="text"
              name="query"
              value={filter.query}
              onChange={handleFilterChange}
              placeholder="Cerca per titolo..."
              className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          
          <div>
            <select
              name="statusId"
              value={filter.statusId}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Tutti gli stati</option>
              {statuses.map(status => (
                <option key={status.id} value={status.id}>
                  {status.name === 'published' ? 'Pubblicato' :
                   status.name === 'draft' ? 'Bozza' : 'Archiviato'}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              name="type"
              value={filter.type}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Tutti i tipi</option>
              <option value="vendita">Vendita</option>
              <option value="affitto">Affitto</option>
            </select>
          </div>
          
          <div>
            <select
              name="zone"
              value={filter.zone}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Tutte le zone</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}, {zone.provinces?.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <button
              onClick={handleSearch}
              className="w-full bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtra
            </button>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <button
            onClick={handleResetFilters}
            className="text-gray-600 hover:text-gray-900 text-sm text-left py-1"
          >
            Resetta filtri
          </button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 flex-shrink-0">Ordina per:</span>
            <select
              value={`${filter.sortBy}-${filter.sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setFilter(prev => ({ ...prev, sortBy: field, sortOrder: order as 'asc' | 'desc' }));
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary w-full sm:w-auto"
            >
              <option value="priority-asc">Priorità (crescente)</option>
              <option value="priority-desc">Priorità (decrescente)</option>
              <option value="created_at-desc">Data (più recenti)</option>
              <option value="created_at-asc">Data (più vecchi)</option>
              <option value="title-asc">Titolo (A-Z)</option>
              <option value="title-desc">Titolo (Z-A)</option>
              <option value="price-asc">Prezzo (crescente)</option>
              <option value="price-desc">Prezzo (decrescente)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Saving indicator */}
      <AnimatePresence>
        {savingOrder && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed top-20 right-8 bg-green-100 text-green-800 px-4 py-2 rounded-lg shadow-md z-50 flex items-center"
          >
            <div className="h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-2"></div>
            <span>Salvataggio ordine...</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-2 text-gray-600">Caricamento immobili...</p>
          </div>
        ) : (
          <>
          {/* Vista card: la tabella a 8 colonne non è consultabile sotto md */}
          <div className="md:hidden divide-y">
            {properties.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Nessun immobile trovato</div>
            ) : (
              properties.map((property, index) => (
                <div key={property.id} className="p-4">
                  <div className="flex gap-3">
                    <div className="h-16 w-16 flex-shrink-0 bg-gray-200 rounded-md overflow-hidden">
                      {property.property_images && property.property_images.length > 0 ? (
                        <img
                          src={property.property_images.find((img: any) => img.is_primary)?.url ||
                               property.property_images[0]?.url}
                          alt={property.title}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <AlignJustify className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="text-gray-900 font-medium leading-snug">{property.title}</div>
                      {property.reference_number && (
                        <div className="font-mono text-xs text-gray-500 mt-0.5">
                          {property.reference_number}
                        </div>
                      )}
                      <div className="font-semibold text-gray-900 mt-1">
                        {getPropertyPriceLabel(property)}
                        {hasPublicPrice(property) && property.type === 'affitto' && '/mese'}
                      </div>
                      <div className="text-sm text-gray-600 mt-0.5">
                        {property.zones?.name}{property.provinces?.name ? `, ${property.provinces.name}` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      property.type === 'vendita'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {property.type === 'vendita' ? 'Vendita' : 'Affitto'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      property.property_statuses?.name === 'published'
                        ? 'bg-green-100 text-green-800'
                        : property.property_statuses?.name === 'draft'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {property.property_statuses?.name === 'published' ? 'Pubblicato' :
                       property.property_statuses?.name === 'draft' ? 'Bozza' : 'Archiviato'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveProperty(index, -1)}
                        disabled={index === 0}
                        className="p-2.5 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        title="Sposta su"
                        aria-label="Sposta su"
                      >
                        <ArrowUp className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => moveProperty(index, 1)}
                        disabled={index === properties.length - 1}
                        className="p-2.5 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        title="Sposta giù"
                        aria-label="Sposta giù"
                      >
                        <ArrowDown className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex items-center gap-1">
                      <a
                        href={`/immobile/${property.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
                        title="Visualizza"
                        aria-label="Visualizza"
                      >
                        <Eye className="w-5 h-5" />
                      </a>
                      {previewTokenOf(property) && (
                        <button
                          onClick={() => copyPreviewLink(previewTokenOf(property)!)}
                          className="p-2.5 rounded-md text-secondary hover:bg-gray-100 transition-colors"
                          title="Copia il link privato"
                          aria-label="Copia il link privato"
                        >
                          <Link2 className="w-5 h-5" />
                        </button>
                      )}
                      <a
                        href={`/admin/properties/${property.id}/edit`}
                        className="p-2.5 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Modifica"
                        aria-label="Modifica"
                      >
                        <Edit2 className="w-5 h-5" />
                      </a>
                      <button
                        onClick={() => handleDelete(property.id)}
                        className="p-2.5 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                        title="Elimina"
                        aria-label="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 w-12">
                    <div className="text-center">
                      <GripVertical className="inline-block h-5 w-5 text-gray-400" />
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Titolo</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Riferimento</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Tipo</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Prezzo</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Zona</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Stato</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {properties.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      Nessun immobile trovato
                    </td>
                  </tr>
                ) : (
                  properties.map((property, index) => (
                    <tr 
                      key={property.id} 
                      className={`border-t cursor-grab active:cursor-grabbing
                        ${isDragging && draggedItemId === property.id ? 'opacity-50 bg-gray-100' : ''}
                        ${draggedOverId === property.id ? 'border-b-2 border-secondary' : 'hover:bg-gray-50'}
                      `}
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, property.id)}
                      onDragOver={(e) => handleDragOver(e, property.id)}
                      onDragEnd={handleDragEnd}
                      data-index={index}
                    >
                      <td className="py-4 px-4 w-12">
                        <div className="flex items-center justify-center cursor-grab active:cursor-grabbing">
                          <div className="p-2 rounded-md hover:bg-gray-100 transition-colors">
                            <GripVertical className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="h-12 w-12 flex-shrink-0 mr-3 bg-gray-200 rounded-md overflow-hidden">
                            {property.property_images && property.property_images.length > 0 ? (
                              <img
                                src={property.property_images.find((img: any) => img.is_primary)?.url || 
                                     property.property_images[0]?.url}
                                alt={property.title}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <AlignJustify className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="text-gray-900 font-medium">{property.title}</div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {property.reference_number ? (
                          <span className="font-mono text-sm text-gray-700">{property.reference_number}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-sm ${
                          property.type === 'vendita'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {property.type === 'vendita' ? 'Vendita' : 'Affitto'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-medium">
                          {getPropertyPriceLabel(property)}
                          {hasPublicPrice(property) && property.type === 'affitto' && '/mese'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {property.zones?.name}, {property.provinces?.name}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-sm ${
                          property.property_statuses?.name === 'published'
                            ? 'bg-green-100 text-green-800'
                            : property.property_statuses?.name === 'draft'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {property.property_statuses?.name === 'published' ? 'Pubblicato' : 
                           property.property_statuses?.name === 'draft' ? 'Bozza' : 'Archiviato'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex space-x-2">
                          <a
                            href={`/immobile/${property.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900"
                            title="Visualizza"
                          >
                            <Eye className="w-5 h-5" />
                          </a>
                          {previewTokenOf(property) && (
                            <button
                              onClick={() => copyPreviewLink(previewTokenOf(property)!)}
                              className="text-secondary hover:text-secondary-dark"
                              title="Copia il link privato"
                            >
                              <Link2 className="w-5 h-5" />
                            </button>
                          )}
                          <a
                            href={`/admin/properties/${property.id}/edit`}
                            className="text-blue-600 hover:text-blue-700"
                            title="Modifica"
                          >
                            <Edit2 className="w-5 h-5" />
                          </a>
                          <button
                            onClick={() => handleDelete(property.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Elimina"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Instructions for drag and drop — solo desktop: su touch il drag non si attiva */}
      <div className="mt-4 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 hidden md:block">
        <div className="flex items-center">
          <div className="bg-blue-100 p-2 rounded-full mr-3">
            <GripVertical className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-blue-800 font-medium">Riordinare gli Immobili</h3>
            <p className="text-blue-600 mt-1">
              Trascina le righe utilizzando l'icona di trascinamento per modificare l'ordine degli immobili.
              L'ordine viene salvato automaticamente e determina come gli immobili appariranno nel sito,
              con priorità a quelli in cima alla lista.
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default PropertiesList;